import { useRef, useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from 'recharts';
import { ArrowUpRight, ArrowDownRight, Minus, Share2, Loader2 } from 'lucide-react';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';

const MONTH_LABEL_MAP = {
  Jan: 'jan', Feb: 'fev', Mar: 'mar', Apr: 'abr', May: 'mai', Jun: 'jun',
  Jul: 'jul', Aug: 'ago', Sep: 'set', Oct: 'out', Nov: 'nov', Dec: 'dez',
};
const ptMonth = (label) => {
  if (!label) return label;
  const [en, yy] = label.split('/');
  return `${MONTH_LABEL_MAP[en] || en}/${yy}`;
};

export const KPIHistoryModal = ({
  open, onClose, metric, monthlyEvolution, theme, loading, companyName,
}) => {
  const { dm } = theme;
  const captureRef = useRef(null);
  const [sharing, setSharing] = useState(false);

  if (!metric) return null;

  const series = (monthlyEvolution || []).map((m) => ({
    ...m,
    label: ptMonth(m.month),
    value: metric.getValue(m),
  }));

  const values = series.map((s) => s.value).filter((v) => typeof v === 'number');
  const hasData = values.length >= 2;
  const current = series[series.length - 1]?.value ?? 0;
  const previous = series[series.length - 2]?.value ?? 0;
  const diff = current - previous;
  const goodDirection = metric.lowerIsBetter ? diff < 0 : diff > 0;
  const isFlat = Math.abs(diff) < 0.01;

  const best = hasData ? (metric.lowerIsBetter ? Math.min(...values) : Math.max(...values)) : 0;
  const worst = hasData ? (metric.lowerIsBetter ? Math.max(...values) : Math.min(...values)) : 0;
  const hasVariation = hasData && best !== worst;
  const bestMonth = hasVariation ? series.find((s) => s.value === best) : null;
  const worstMonth = hasVariation ? series.find((s) => s.value === worst) : null;

  const trendColor = isFlat ? '#94A3B8' : goodDirection ? '#10B981' : '#EF4444';
  const trendBg = isFlat
    ? 'bg-slate-500/10 text-slate-500'
    : goodDirection
      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
      : 'bg-red-500/10 text-red-600 dark:text-red-400';

  const fmt = (v) => (v === null || v === undefined ? '—' : metric.format(v));

  // Data para o snapshot compartilhável
  const shareDate = new Date().toLocaleDateString('pt-BR', {
    day: '2-digit', month: 'long', year: 'numeric',
  });

  const buildCaption = () => {
    const brand = companyName ? `📊 *${companyName}* — Evolução ${metric.label}\n` : `📊 Evolução ${metric.label}\n`;
    let story = '';
    if (hasData && !isFlat) {
      const verb = goodDirection ? 'Melhoramos' : 'Precisamos atenção:';
      story = `\n${verb} ${metric.formatDelta(Math.abs(diff))} vs. o mês anterior.`;
    }
    return `${brand}Valor atual: ${fmt(current)}${story}\n\nGerado em ${shareDate} · Falo com o Dono`;
  };

  const handleShare = async () => {
    if (!captureRef.current || sharing) return;
    setSharing(true);
    try {
      // Aguarda o próximo frame para garantir que o gráfico esteja renderizado
      await new Promise((r) => requestAnimationFrame(r));
      const dataUrl = await toPng(captureRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: dm ? '#0B1120' : '#FFFFFF',
      });
      // Converte dataUrl para Blob (necessário para Web Share e download)
      const blob = await (await fetch(dataUrl)).blob();
      const fileName = `evolucao-${metric.key}-${Date.now()}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });

      const caption = buildCaption();

      // Estratégia 1: Web Share API com arquivos (mobile moderno) → 1 toque pra WhatsApp
      if (
        typeof navigator !== 'undefined'
        && navigator.canShare
        && navigator.canShare({ files: [file] })
      ) {
        try {
          await navigator.share({
            files: [file],
            title: `Evolução ${metric.label}`,
            text: caption,
          });
          setSharing(false);
          return;
        } catch (err) {
          // Usuário cancelou (AbortError) ou browser negou → segue para fallback
          if (err && err.name === 'AbortError') {
            setSharing(false);
            return;
          }
        }
      }

      // Estratégia 2 (fallback): baixa o PNG e abre o WhatsApp com a legenda
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Imagem salva! Anexe no WhatsApp da equipe.', { duration: 4000 });
      // Abre WhatsApp com legenda pronta (usuário anexa a imagem manualmente)
      window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, '_blank');
    } catch (err) {
      console.error('Erro ao gerar imagem:', err);
      toast.error('Não foi possível gerar a imagem. Tente novamente.');
    } finally {
      setSharing(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className={`w-full sm:max-w-lg overflow-y-auto ${dm ? 'bg-[#0B1120] border-slate-800 text-slate-100' : 'bg-white'}`}
        data-testid="kpi-history-modal"
      >
        <div ref={captureRef} className={`p-1 -m-1 ${dm ? 'bg-[#0B1120]' : 'bg-white'}`}>
        <SheetHeader className="text-left">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${metric.color}20`, color: metric.color }}
            >
              <metric.Icon className="w-6 h-6" />
            </div>
            <div className="min-w-0">
              <SheetTitle className={dm ? 'text-slate-100' : 'text-slate-900'}>
                {metric.label}
              </SheetTitle>
              <SheetDescription className={dm ? 'text-slate-400' : 'text-slate-500'}>
                {companyName ? `${companyName} · ` : ''}Evolução dos últimos 6 meses
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Headline */}
        <div className="mt-6 mb-4">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className={`text-4xl font-extrabold tabular-nums ${dm ? 'text-slate-100' : 'text-slate-900'}`}>
              {fmt(current)}
            </span>
            {hasData && !isFlat && (
              <span className={`inline-flex items-center gap-1 text-sm font-bold px-2 py-1 rounded-md ${trendBg}`}>
                {goodDirection ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                {metric.formatDelta(Math.abs(diff))}
              </span>
            )}
            {hasData && isFlat && (
              <span className={`inline-flex items-center gap-1 text-sm font-bold px-2 py-1 rounded-md ${trendBg}`}>
                <Minus className="w-4 h-4" />
                estável
              </span>
            )}
          </div>
          <p className={`text-sm mt-1 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
            {hasData
              ? (isFlat
                ? `Ficou igual ao mês anterior (${fmt(previous)})`
                : (goodDirection
                  ? `Melhorou ${metric.formatDelta(Math.abs(diff))} vs. o mês anterior (${fmt(previous)})`
                  : `Piorou ${metric.formatDelta(Math.abs(diff))} vs. o mês anterior (${fmt(previous)})`))
              : 'Ainda não temos dados suficientes para comparar meses.'}
          </p>
        </div>

        {/* Chart */}
        {loading ? (
          <div className={`h-64 flex items-center justify-center text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
            Carregando histórico...
          </div>
        ) : hasData ? (
          <div className={`rounded-xl border p-3 mb-5 ${dm ? 'border-slate-700 bg-slate-900/40' : 'border-slate-200 bg-slate-50/50'}`}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={series} margin={{ top: 12, right: 16, left: -12, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={dm ? '#1E293B' : '#E2E8F0'} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: dm ? '#94A3B8' : '#64748B' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: dm ? '#94A3B8' : '#64748B' }} axisLine={false} tickLine={false} width={40} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: dm ? '#1E293B' : '#FFFFFF',
                    border: `1px solid ${dm ? '#334155' : '#E2E8F0'}`,
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelStyle={{ color: dm ? '#F1F5F9' : '#0F172A', fontWeight: 600 }}
                  formatter={(v) => [metric.format(v), metric.label]}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={metric.color}
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: metric.color, strokeWidth: 0 }}
                  activeDot={{ r: 6 }}
                />
                {bestMonth && (
                  <ReferenceDot x={bestMonth.label} y={bestMonth.value} r={5} fill="#10B981" stroke="#FFF" strokeWidth={2} />
                )}
                {worstMonth && (
                  <ReferenceDot x={worstMonth.label} y={worstMonth.value} r={5} fill="#EF4444" stroke="#FFF" strokeWidth={2} />
                )}
              </LineChart>
            </ResponsiveContainer>
            {hasVariation && (
              <div className={`flex items-center gap-4 text-[11px] mt-2 px-2 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" /> Melhor mês
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-red-500" /> Pior mês
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className={`h-40 rounded-xl border-2 border-dashed flex items-center justify-center text-sm ${dm ? 'border-slate-700 text-slate-500' : 'border-slate-200 text-slate-400'}`}>
            Ainda não há dados suficientes para exibir o gráfico.
          </div>
        )}

        {/* Monthly cards */}
        <div className={`text-xs font-semibold uppercase tracking-wider mb-2 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
          Mês a mês
        </div>
        <div className="grid grid-cols-3 gap-2" data-testid="kpi-history-months">
          {series.map((s, idx) => {
            const prev = idx > 0 ? series[idx - 1].value : null;
            const d = prev !== null ? s.value - prev : null;
            const good = metric.lowerIsBetter ? d < 0 : d > 0;
            const flat = d !== null && Math.abs(d) < 0.01;
            const isBest = hasData && s.value === best && best !== worst;
            const isWorst = hasData && s.value === worst && best !== worst;
            return (
              <div
                key={`${s.year}-${s.month_num}`}
                className={`rounded-lg border p-2.5 ${
                  isBest
                    ? (dm ? 'border-emerald-700/60 bg-emerald-900/20' : 'border-emerald-300 bg-emerald-50')
                    : isWorst
                      ? (dm ? 'border-red-800/60 bg-red-900/20' : 'border-red-300 bg-red-50')
                      : (dm ? 'border-slate-700 bg-slate-900/40' : 'border-slate-200 bg-white')
                }`}
              >
                <p className={`text-[10px] font-semibold uppercase tracking-wide ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
                  {s.label}
                </p>
                <p className={`text-lg font-bold tabular-nums mt-0.5 ${dm ? 'text-slate-100' : 'text-slate-900'}`}>
                  {fmt(s.value)}
                </p>
                {d !== null && !flat && (
                  <p className={`text-[10px] font-semibold flex items-center gap-0.5 mt-0.5 ${good ? 'text-emerald-500' : 'text-red-500'}`}>
                    {good ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                    {metric.formatDelta(Math.abs(d))}
                  </p>
                )}
                {isBest && <p className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 uppercase mt-0.5">★ Melhor</p>}
                {isWorst && <p className="text-[9px] font-bold text-red-600 dark:text-red-400 uppercase mt-0.5">! Pior</p>}
              </div>
            );
          })}
        </div>

        <div className={`text-[11px] mt-4 leading-relaxed ${dm ? 'text-slate-500' : 'text-slate-400'}`}>
          💡 Dica: mostre esta tela para sua equipe e discuta o que impactou os melhores e piores meses. Use os aprendizados para planejar as próximas ações.
        </div>

        {/* Rodapé branded — visível também no PNG compartilhado */}
        <div className={`mt-5 pt-4 border-t flex items-center justify-between gap-3 ${dm ? 'border-slate-800' : 'border-slate-100'}`}>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-[#0A2647] text-[#C5A059] flex items-center justify-center shrink-0 text-[11px] font-black">★</div>
            <div className="min-w-0 leading-tight">
              <p className={`text-[11px] font-bold truncate ${dm ? 'text-slate-200' : 'text-[#0A2647]'}`}>Falo com o Dono</p>
              <p className={`text-[10px] truncate ${dm ? 'text-slate-500' : 'text-slate-400'}`}>Relatório gerado em {shareDate}</p>
            </div>
          </div>
        </div>
        </div>
        {/* /captureRef */}

        {/* Botão de compartilhamento — fica FORA do captureRef para não aparecer no PNG */}
        <Button
          onClick={handleShare}
          disabled={sharing || loading}
          className="w-full mt-5 gap-2 bg-[#0A2647] hover:bg-[#143057] text-white font-semibold rounded-xl h-11"
          data-testid="kpi-share-btn"
        >
          {sharing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Gerando imagem...
            </>
          ) : (
            <>
              <Share2 className="w-4 h-4" />
              Compartilhar com equipe
            </>
          )}
        </Button>
        <p className={`text-[11px] text-center mt-2 mb-2 ${dm ? 'text-slate-500' : 'text-slate-400'}`}>
          Gera um PNG desta tela e abre pronto para enviar ao WhatsApp da equipe.
        </p>
      </SheetContent>
    </Sheet>
  );
};
